import Papa from 'papaparse';
import _ from 'lodash';
import {NormalizationPreviewWellStatus, NormalizationType, TaskType, WellIncrementPattern} from '../constants/dataEnum';
import {find} from './objectHelper';
import {setDefaultTipTouchOffDirectionTasks} from './PipettingProfileItemHelper';

export const generateNormalizationPreviewData = (normalizationState, labwareState) => {
  const {
    csvData,
    startRow,
    selectedColumnIndex,
    wellIncrementPattern,
    sampleLabwareID,
    currentVolume,
    targetConcentration,
    type,
  } = normalizationState;

  const selectedLabware = find(labwareState, {LabwareID: sampleLabwareID}) ?? null;

  // find lowest concentration on selected data

  let dataLength = Math.min(selectedLabware.colCount * selectedLabware.rowCount, csvData.length);

  // get selected data
  const selectedData = [];
  if (dataLength > csvData.length - startRow) {
    dataLength = csvData.length - startRow;
  }

  for (let row = startRow; row < dataLength + startRow; row += 1) {
    selectedData.push(csvData[row][selectedColumnIndex]);
  }

  // generate preview data
  const previewData = [];
  for (let idx = 0; idx < selectedData.length; idx += 1) {
    if (type === NormalizationType.FIXED_SAMPLE_VOLUME) {
      let diluentVolume = 0.0;
      if (targetConcentration < selectedData[idx]) {
        diluentVolume = (currentVolume * selectedData[idx]) / targetConcentration - currentVolume;
        diluentVolume = Math.round(diluentVolume * 10) / 10;
      }

      // calculate well status based on diluent volume and plate's working capacity
      let wellStatus = NormalizationPreviewWellStatus.NORMAL;
      if (diluentVolume + currentVolume > selectedLabware.workingCapacity || targetConcentration > selectedData[idx]) {
        wellStatus = NormalizationPreviewWellStatus.INVALID;
      } else if (diluentVolume <= 0) {
        wellStatus = NormalizationPreviewWellStatus.SKIPPED;
      }

      let rowNumber = 0;
      let colNumber = 0;
      if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_ROW) {
        rowNumber = Math.floor(idx / selectedLabware.colCount);
        colNumber = idx % selectedLabware.colCount;
      } else {
        colNumber = Math.floor(idx / selectedLabware.rowCount);
        rowNumber = idx % selectedLabware.rowCount;
      }

      // selectedLabware.colCount & selectedLabware.rowCount is misnamed (switched)
      previewData.push({
        row: rowNumber + 1,
        col: colNumber + 1,
        reagentCurrentConcentration: selectedData[idx],
        volume: diluentVolume,
        status: wellStatus,
      });
    } else {
      let sampleVolume = 0.0;
      if (currentVolume > 0 && selectedData[idx] > targetConcentration) {
        sampleVolume = (currentVolume * targetConcentration) / (selectedData[idx] - targetConcentration);
        sampleVolume = Math.round(sampleVolume * 10) / 10;
      }
      // calculate well status based on diluent volume and plate's working capacity
      let wellStatus = NormalizationPreviewWellStatus.NORMAL;
      if (sampleVolume + currentVolume > selectedLabware.workingCapacity || targetConcentration > selectedData[idx]) {
        wellStatus = NormalizationPreviewWellStatus.INVALID;
      } else if (sampleVolume <= 0) {
        wellStatus = NormalizationPreviewWellStatus.SKIPPED;
      }

      let rowNumber = 0;
      let colNumber = 0;
      if (wellIncrementPattern === WellIncrementPattern.ORDER_BY_ROW) {
        rowNumber = Math.floor(idx / selectedLabware.colCount);
        colNumber = idx % selectedLabware.colCount;
      } else {
        colNumber = Math.floor(idx / selectedLabware.rowCount);
        rowNumber = idx % selectedLabware.rowCount;
      }
      /* Rumus molaritas formula
        V0 * M0 = Vt * Mt
        This is used to check the calculations are correct or not, for debugging purpose
        also for correct values remove the Math.round() function
        console.log('molarity TEst', sampleVolume * selectedData[idx], ', ', (sampleVolume + currentVolume) * targetConcentration);
      */
      // selectedLabware.colCount & selectedLabware.rowCount is misnamed (switched)
      previewData.push({
        row: rowNumber + 1,
        col: colNumber + 1,
        reagentCurrentConcentration: selectedData[idx],
        volume: sampleVolume,
        status: wellStatus,
      });
    }
  }
  console.log('Normalization preview data:', previewData);
  return previewData;
};

export const getCSVParsedData = (rawData, delimiter) => {
  const parsedData = Papa.parse(rawData, {
    delimiter,
    dynamicTyping: true,
    skipEmptyLines: 'greedy',
  });

  return parsedData;
};

export const getLabwareRowAndCol = (row, col, labware) => {
  let calculatedColumn = col;
  let calculatedRow = row;
  if (row > labware.rowCount) {
    calculatedRow = row % labware.rowCount === 0 ? labware.rowCount : row % labware.rowCount;
  }
  if (col > labware.colCount) {
    calculatedColumn = col % labware.colCount === 0 ? labware.colCount : col % labware.colCount;
    calculatedRow = labware.rowCount + 1 - row;
  }
  return {calculatedRow, calculatedColumn};
};

export const mergeNormalizationLabwareWellColor = (labwareID, labwareColor, colorMap) => {
  const mergedColor = _.cloneDeep(colorMap);
  if (!mergedColor[labwareID] && labwareColor[labwareID]) {
    mergedColor[labwareID] = _.cloneDeep(labwareColor[labwareID]);
  } else if (labwareColor[labwareID] && mergedColor[labwareID]) {
    Object.keys(labwareColor[labwareID]).forEach((well) => {
      mergedColor[labwareID][well] = _.cloneDeep(labwareColor[labwareID][well]);
    });
  }
  return mergedColor;
};

export const taskListNormalizationGenerator = (
  previewData,
  maxAspirateVolume,
  startingID,
  source,
  target,
  normalizationType,
  pipettingParams,
  protocolEjectTipLocation,
) => {
  const {aspirate, dispense} = pipettingParams;

  const newTaskList = [];
  let newTaskID = startingID;
  let number = 1;
  const {
    sourceLabware,
    row: sourceRow,
    column: sourceColumn,
    labwareID: sourceLabwareID,
    tipPosition: minTipPositionSourceLabware,
  } = source;
  const {targetLabware, labwareID: targetLabwareID, tipPosition: minTipPositionTargetLabware} = target;

  const isSkipRowAndCol =
    (sourceLabware.rowCount < targetLabware.rowCount || sourceLabware.colCount < targetLabware.colCount) &&
    normalizationType === NormalizationType.FIXED_DILUENT_VOLUME;

  for (let iPrev = 0; iPrev < previewData.length; iPrev += 1) {
    const {status, row, col, volume: diluentVol} = previewData[iPrev];

    if (status === NormalizationPreviewWellStatus.NORMAL) {
      const cycle = 1;
      const volume = diluentVol;
      const mixingVolume = volume > maxAspirateVolume.value ? maxAspirateVolume.value : volume;

      for (let index = 0; index < cycle; index++) {
        // add pick tip task
        newTaskList.push({
          ID: newTaskID++,
          type: TaskType.PICK_TIP,
          columnTipCount: 1,
          rowTipCount: 1,
          row: '',
          column: '',
          number,
          labwareID: 2,
        });

        // add aspirate task
        const aspirateTask = {
          ID: newTaskID++,
          type: TaskType.ASPIRATE,
          columnTipCount: aspirate?.columnTipCount ?? 1,
          rowTipCount: aspirate?.rowTipCount ?? 1,
          row: normalizationType === NormalizationType.FIXED_SAMPLE_VOLUME ? sourceRow : String.fromCharCode(64 + row),
          column: normalizationType === NormalizationType.FIXED_SAMPLE_VOLUME ? sourceColumn : col,
          volume,
          number: number++,
          labwareID: sourceLabwareID,
          tipPosition: minTipPositionSourceLabware,
          speed: aspirate?.speed ?? 3,
          retractSpeed: aspirate?.retractSpeed ?? 20,
          enableMixing: aspirate?.enableMixing ?? false,
          mixingSpeed: aspirate?.mixingSpeed ?? 3,
          mixingCycle: aspirate?.mixingCycle ?? 3,
          mixingVolume: aspirate?.mixingVolume ?? mixingVolume,
          mixingPositionAspirate: aspirate?.mixingPositionAspirate ?? minTipPositionSourceLabware,
          mixingPositionDispense: aspirate?.mixingPositionDispense ?? minTipPositionSourceLabware,
          tipTouchOff: aspirate?.tipTouchOff ?? false,
          tipTouchOffDelay: aspirate?.tipTouchOffDelay ?? 0,
          tipTouchOffHeight: aspirate?.tipTouchOffHeight ?? sourceLabware?.wellDepth,
        };
        const copyAspirateTask = setDefaultTipTouchOffDirectionTasks(sourceLabware, aspirateTask);
        newTaskList.push(copyAspirateTask);

        const rowInteger = typeof sourceRow === 'string' ? sourceRow.charCodeAt(0) - 64 : sourceRow;
        const {calculatedRow, calculatedColumn} = getLabwareRowAndCol(row, col, targetLabware);

        // add dispense task
        const dispenseTask = {
          ID: newTaskID++,
          type: TaskType.DISPENSE,
          row: String.fromCharCode(
            64 + (isSkipRowAndCol ? calculatedRow * 2 - 1 + (rowInteger % 2 === 0 ? 1 : 0) : calculatedRow),
          ),
          columnTipCount: dispense?.columnTipCount ?? 1,
          rowTipCount: dispense?.rowTipCount ?? 1,
          column: isSkipRowAndCol ? calculatedColumn * 2 - 1 + (sourceColumn % 2 === 0 ? 1 : 0) : calculatedColumn,
          number: number++,
          volume,
          labwareID: targetLabwareID,
          tipPosition: minTipPositionTargetLabware,
          speed: dispense?.speed ?? 3,
          retractSpeed: dispense?.retractSpeed ?? 20,
          enableMixing: dispense?.enableMixing ?? true,
          mixingSpeed: dispense?.mixingSpeed ?? 3,
          mixingCycle: dispense?.mixingCycle ?? 3,
          mixingVolume: dispense?.mixingVolume ?? mixingVolume,
          precisionDispense: dispense?.precisionDispense ?? false,
          tipTouchOff: dispense?.tipTouchOff ?? false,
          tipTouchOffDelay: dispense?.tipTouchOffDelay ?? 0,
          dispenseAtWellSide: dispense?.dispenseAtWellSide ?? false,
          mixingPositionAspirate: dispense?.mixingPositionAspirate ?? minTipPositionTargetLabware,
          mixingPositionDispense: dispense?.mixingPositionDispense ?? minTipPositionTargetLabware,
          tipTouchOffHeight: dispense?.tipTouchOffHeight ?? targetLabware?.wellDepth,
        };
        const copyDispenseTask = setDefaultTipTouchOffDirectionTasks(targetLabware, dispenseTask);
        newTaskList.push(copyDispenseTask);

        // add eject tip task
        newTaskList.push({
          ID: newTaskID++,
          type: TaskType.EJECT_TIP,
          number: number++,
          columnTipCount: 1,
          rowTipCount: 1,
          changeTip: true,
          labwareID: protocolEjectTipLocation,
        });
      }
    }
  }

  return newTaskList;
};

export const is1WellTrough = (labware) => labware.colCount === 1 && labware.rowCount === 1;
export const is8WellTrough = (labware) => labware.colCount === 1 && labware.rowCount === 8;
export const is12WellTrough = (labware) => labware.colCount === 12 && labware.rowCount === 1;

export const isCsvNormalizationValid = (parsedData) => {
  const isCsvError = parsedData.errors.length > 0;
  const isCsvHaveNoValue = parsedData.data.length <= 1;

  if (isCsvError || isCsvHaveNoValue) return false;

  return true;
};
